import {
  html,
  attr,
  on,
  Signal,
  TNode,
  Ensure,
  prop,
  When,
  OnDispose,
  Empty,
} from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { authStore, oauth } from '@/services/gateway'
import { Workspace } from '@/state/workspace'

export interface TopbarOptions {
  opened: Signal<boolean>
  setOpen: (value: boolean) => void
}

export function Topbar(
  { opened, setOpen }: TopbarOptions,
  ...children: TNode[]
) {
  return html.header(
    attr.class(
      'h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6'
    ),
    // Mobile menu button
    html.button(
      on.click(() => setOpen(!opened.value)),
      attr.class('lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100'),
      Icon({ icon: 'mdi:menu' }, attr.class('w-6 h-6 text-gray-500'))
    ),
    // Page title
    html.h1(attr.class('text-lg font-semibold text-gray-900'), ...children),
    // Right side
    html.div(
      attr.class('flex items-center gap-2 sm:gap-4'),
      // Credits badge - links to organization billing
      Anchor(
        {
          href: Workspace.organizationId.map((oid) => `/o/${oid}/billing`),
        },
        attr.class(
          'flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer'
        ),
        Icon(
          { icon: 'mdi:circle-multiple' },
          attr.class('w-4 h-4 text-gray-500')
        ),
        html.span(
          attr.class('text-sm font-medium text-gray-700 hidden sm:inline'),
          Workspace.formattedCredits
        )
      ),
      // User menu dropdown
      UserMenu()
    )
  )
}

function UserMenu() {
  const isOpen = prop(false)

  const handleSignOut = async () => {
    try {
      await oauth.logout()
      authStore.clear()
      window.location.href = '/signin'
    } catch (error) {
      console.error('Logout failed:', error)
      authStore.clear()
      window.location.href = '/signin'
    }
  }

  // Close dropdown when clicking outside
  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target.closest('[data-user-menu]')) {
      isOpen.set(false)
    }
  }

  // Set up click outside listener with cleanup
  if (typeof window !== 'undefined') {
    window.addEventListener('click', handleClickOutside)
    OnDispose(() => {
      window.removeEventListener('click', handleClickOutside)
    })
  }

  return html.div(
    attr.data('user-menu'),
    attr.class('relative'),
    // Avatar button
    Ensure(authStore.user, (user) =>
      html.button(
        on.click((e) => {
          e.stopPropagation()
          isOpen.set(!isOpen.get())
        }),
        attr.class(
          'w-8 h-8 rounded-full bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors flex items-center justify-center'
        ),
        When(
          user.map((u) => !!u.avatarUrl),
          () =>
            html.img(
              attr.src(user.map((u) => u.avatarUrl || '')),
              attr.alt('Avatar'),
              attr.class('w-8 h-8 rounded-full object-cover')
            ),
          () =>
            html.span(
              user.map((u) =>
                u.displayName
                  ? u.displayName
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .toUpperCase()
                  : u.email.charAt(0).toUpperCase()
              )
            )
        )
      )
    ),
    // Dropdown menu
    When(
      isOpen,
      () =>
        html.div(
          attr.class(
            'absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50'
          ),
          // User info
          Ensure(authStore.user, (user) =>
            html.div(
              attr.class('px-4 py-3 border-b border-gray-100'),
              html.p(
                attr.class('text-sm font-medium text-gray-900 truncate'),
                user.map((u) => u.displayName || u.email)
              ),
              html.p(
                attr.class('text-xs text-gray-500 truncate'),
                user.map((u) => u.email)
              )
            )
          ),
          // Menu items
          html.button(
            on.click(() => {
              isOpen.set(false)
              NavigationService.navigate('/profile')
            }),
            attr.class(
              'w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'
            ),
            Icon({ icon: 'mdi:account-circle' }, attr.class('w-4 h-4')),
            'Profile'
          ),
          html.button(
            on.click(() => {
              isOpen.set(false)
              handleSignOut()
            }),
            attr.class(
              'w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'
            ),
            Icon({ icon: 'mdi:logout' }, attr.class('w-4 h-4')),
            'Sign Out'
          )
        ),
      () => Empty
    )
  )
}
